import { useCallback, useState } from 'react';
import { getFileURL, uploadFile, uploadMultipleFiles } from '../service/uploadDownloadService';
import logUtils from './LogUtils';
import { download } from './UtilityFunctions';

const allowedFiletypes = ['xls', 'xlsx', 'csv', 'doc', 'docx', 'txt', 'jpeg', 'jpg', 'png', 'pdf'];

export const useDownloadButtonClicked = (fileObj) => {
  const onDownloadButtonClicked = useCallback(async () => {
    if (!fileObj.key) {
      logUtils.logError(new Error('Trying to download file which is not uploaded yet'), 'Cannot download file');
      return;
    }
    try {
      const signedUrl = await getFileURL(fileObj);
      download(signedUrl);
    } catch (e) {
      logUtils.logError(e, 'Could not download file');
    }
  }, [fileObj]);
  return onDownloadButtonClicked;
};

export const useAttachmentFileChangeHook = (onAttachmentUpload, fileSizeLimitInMB, handleError, loggedInUser) => {
  const [uploading, setUploading] = useState(false);
  const onFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      handleError(null);
      const fileType = file.name.split('.')[1];

      if (!allowedFiletypes.includes(fileType)) {
        e.target.value = null;
        handleError('File type not supported');
        return;
      }
      if (!file) return;
      if (file.size / (1024 * 1024) > fileSizeLimitInMB) {
        e.target.value = null;
        handleError('File size limit exceeded');
        return;
      }
      setUploading(true);
      try {
        const fileObject = await uploadFile({ file });
        onAttachmentUpload(fileObject);
      } catch (error) {
        logUtils.logError(error, 'Could not upload file');
      } finally {
        setUploading(false);
      }
      handleError(null);
    },
    [loggedInUser, handleError, setUploading, onAttachmentUpload, fileSizeLimitInMB]
  );
  return [uploading, onFileChange];
};

export const useMultiAttachmentFileChangeHook = ({
  onAttachmentUpload,
  fileSizeLimitInMB,
  handleError,
  loggedInUser,
  maxLimitNumberOfFiles,
  uploadedFilesLength = 0,
}) => {
  const [uploading, setUploading] = useState(false);
  const onFileChange = useCallback(
    async (e) => {
      const { files } = e.target;
      if (files?.length > 10) {
        handleError('Too many files selected: Attach up to 10 files At a Time.');
        return;
      }
      if (uploadedFilesLength + files?.length > maxLimitNumberOfFiles) {
        handleError('Max Files For a single Element cannot exceed 50');
        return;
      }
      handleError(null);
      for (const file of files) {
        if (!file) return;
        const fileType = file.name.split('.')[1];

        if (!allowedFiletypes.includes(fileType)) {
          e.target.value = null;
          handleError('File type not supported');
          return;
        }
        if (file.size / (1024 * 1024) > fileSizeLimitInMB) {
          e.target.value = null;
          handleError('File size limit exceeded');
          return;
        }
      }

      setUploading(true);
      try {
        const { processed = [], unprocessed = [] } = await uploadMultipleFiles({ files });
        onAttachmentUpload(processed);
        if (unprocessed?.length) {
          const failedFileNameArr = unprocessed.map(({ fileName }) => fileName);
          const failedFileNames = failedFileNameArr.join(', ');
          handleError(`Filed To Upload ${unprocessed.length} Files: ${failedFileNames}`);
        }
      } catch (error) {
        logUtils.logError(error, 'Could not upload file');
      } finally {
        setUploading(false);
      }
      handleError(null);
    },
    [uploadedFilesLength, loggedInUser, handleError, setUploading, onAttachmentUpload, fileSizeLimitInMB]
  );
  return [uploading, onFileChange];
};
